import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const API = axios.create({ baseURL: '/api/v1/mmsp' });
API.interceptors.request.use((cfg) => {
  const token = sessionStorage.getItem('portal_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

function apiErrorMessage(err: unknown, fallback: string) {
  const data = (err as { response?: { data?: { message?: string; error?: string } } })?.response?.data;
  return data?.message || data?.error || (err as { message?: string })?.message || fallback;
}

interface CommunityRow {
  id: number;
  name: string;
  slug: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

const EMPTY_FORM = {
  name: '',
  contributionAmountZar: '200',
  contributionFrequency: 'monthly',
  chairMsisdn: '',
  treasurerMsisdn: '',
  secretaryMsisdn: '',
  communityName: '',
};

export const MmspClubRegisterModal: React.FC<Props> = ({ open, onClose, onCreated }) => {
  const [communities, setCommunities] = useState<CommunityRow[]>([]);
  const [communityId, setCommunityId] = useState('');
  const [communityQuery, setCommunityQuery] = useState('');
  const [newCommunity, setNewCommunity] = useState(false);
  const [showSuggested, setShowSuggested] = useState(false);
  const [showCommittee, setShowCommittee] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState<{ setupUrl?: string; clubCode?: string } | null>(null);

  useEffect(() => {
    if (!open) return;
    setCreated(null);
    setError('');
    setForm(EMPTY_FORM);
    setCommunityId('');
    setCommunityQuery('');
    setNewCommunity(false);
    setShowSuggested(false);
    setShowCommittee(false);
    API.get('/ops/communities')
      .then((res) => setCommunities(res.data?.data || []))
      .catch((err: unknown) => {
        setCommunities([]);
        setError(apiErrorMessage(err, 'Could not load communities.'));
      });
  }, [open]);

  const filtered = useMemo(() => {
    const q = communityQuery.trim().toLowerCase();
    if (!q) return communities;
    return communities.filter((c) => c.name.toLowerCase().includes(q));
  }, [communities, communityQuery]);

  if (!open) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!newCommunity && !communityId) {
      setError('Pick a community, or tick that this club’s community is not in the list.');
      return;
    }
    if (newCommunity && !form.communityName.trim()) {
      setError('Enter the community display name.');
      return;
    }
    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        name: form.name.trim(),
        chairMsisdn: form.chairMsisdn.trim(),
        treasurerMsisdn: form.treasurerMsisdn.trim() || undefined,
        secretaryMsisdn: form.secretaryMsisdn.trim() || undefined,
      };
      if (showSuggested) {
        payload.contributionAmountZar = Number(form.contributionAmountZar || 200);
        payload.contributionFrequency = form.contributionFrequency;
      }
      if (newCommunity) {
        payload.community = { name: form.communityName.trim() };
      } else {
        payload.communityId = Number(communityId);
      }
      const res = await API.post('/ops/clubs/register', payload);
      setCreated({
        setupUrl: res.data?.data?.chairInvite?.setupUrl,
        clubCode: res.data?.data?.club?.clubCode,
      });
      onCreated();
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Could not register this club.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="mmsp-register-title">
      <div className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-xl bg-white p-6 shadow-lg">
        <h2 id="mmsp-register-title" className="text-lg font-semibold">Register a club</h2>
        <p className="mt-1 text-sm text-[var(--muted-foreground)]">
          Creates a draft. The chair sets chapters. Activation opens the pool.
        </p>
        {created ? (
          <div className="mt-4 space-y-3 text-sm">
            <p>Draft registered. Club code <strong>{created.clubCode}</strong>.</p>
            <p className="break-all">Chair setup: {created.setupUrl}</p>
            <button type="button" className="min-h-[44px] rounded-lg bg-[var(--primary)] px-4 py-2 text-white" onClick={onClose}>Done</button>
          </div>
        ) : (
          <form className="mt-4 grid gap-3" onSubmit={submit}>
            <label htmlFor="mmsp-chair" className="text-sm font-medium">Chair mobile</label>
            <input
              id="mmsp-chair"
              className="min-h-[44px] rounded-lg border px-3 py-2"
              placeholder="Existing MyMoolah wallet"
              value={form.chairMsisdn}
              onChange={(e) => setForm({ ...form, chairMsisdn: e.target.value })}
              inputMode="tel"
              autoComplete="tel"
              required
            />

            <label htmlFor="mmsp-club-name" className="text-sm font-medium">Club name</label>
            <input
              id="mmsp-club-name"
              className="min-h-[44px] rounded-lg border px-3 py-2"
              placeholder="The stokvel’s name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />

            <label className="flex min-h-[44px] items-center gap-2 text-sm">
              <input type="checkbox" checked={newCommunity} onChange={(e) => setNewCommunity(e.target.checked)} />
              This club’s community is not in the list
            </label>

            {newCommunity ? (
              <>
                <label htmlFor="mmsp-community-name" className="text-sm font-medium">Community display name</label>
                <input
                  id="mmsp-community-name"
                  className="min-h-[44px] rounded-lg border px-3 py-2"
                  placeholder="e.g. St John’s Church Soweto"
                  value={form.communityName}
                  onChange={(e) => setForm({ ...form, communityName: e.target.value })}
                  required
                />
                <p className="text-xs text-[var(--muted-foreground)]">A short name is generated automatically. Members never see it.</p>
              </>
            ) : (
              <>
                <label htmlFor="mmsp-community-search" className="text-sm font-medium">Community</label>
                <input
                  id="mmsp-community-search"
                  className="min-h-[44px] rounded-lg border px-3 py-2"
                  placeholder="Search communities"
                  value={communityQuery}
                  onChange={(e) => setCommunityQuery(e.target.value)}
                />
                <select
                  id="mmsp-community"
                  className="min-h-[44px] rounded-lg border px-3 py-2"
                  value={communityId}
                  onChange={(e) => setCommunityId(e.target.value)}
                  required={!newCommunity}
                  aria-label="Select community"
                >
                  <option value="">Select community</option>
                  {filtered.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </>
            )}

            <button
              type="button"
              className="text-left text-sm text-[var(--mymoolah-blue)]"
              onClick={() => setShowSuggested((v) => !v)}
            >
              {showSuggested ? 'Hide suggested amount' : 'Suggested amount (optional)'}
            </button>
            {showSuggested && (
              <div className="grid grid-cols-2 gap-3">
                <label className="text-sm">
                  Amount (ZAR)
                  <input
                    className="mt-1 min-h-[44px] w-full rounded-lg border px-3 py-2"
                    value={form.contributionAmountZar}
                    onChange={(e) => setForm({ ...form, contributionAmountZar: e.target.value })}
                    inputMode="decimal"
                  />
                </label>
                <label className="text-sm">
                  Frequency
                  <select
                    className="mt-1 min-h-[44px] w-full rounded-lg border px-3 py-2"
                    value={form.contributionFrequency}
                    onChange={(e) => setForm({ ...form, contributionFrequency: e.target.value })}
                  >
                    <option value="weekly">Weekly</option>
                    <option value="fortnightly">Fortnightly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </label>
              </div>
            )}

            <button
              type="button"
              className="text-left text-sm text-[var(--mymoolah-blue)]"
              onClick={() => setShowCommittee((v) => !v)}
            >
              {showCommittee ? 'Hide treasurer and secretary' : 'Treasurer and secretary (optional)'}
            </button>
            {showCommittee && (
              <>
                <label htmlFor="mmsp-treasurer" className="text-sm">Treasurer mobile</label>
                <input
                  id="mmsp-treasurer"
                  className="min-h-[44px] rounded-lg border px-3 py-2"
                  value={form.treasurerMsisdn}
                  onChange={(e) => setForm({ ...form, treasurerMsisdn: e.target.value })}
                  inputMode="tel"
                />
                <label htmlFor="mmsp-secretary" className="text-sm">Secretary mobile</label>
                <input
                  id="mmsp-secretary"
                  className="min-h-[44px] rounded-lg border px-3 py-2"
                  value={form.secretaryMsisdn}
                  onChange={(e) => setForm({ ...form, secretaryMsisdn: e.target.value })}
                  inputMode="tel"
                />
              </>
            )}

            {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
            <div className="flex justify-end gap-2">
              <button type="button" className="min-h-[44px] rounded-lg px-4 py-2" onClick={onClose}>Cancel</button>
              <button type="submit" disabled={submitting} className="min-h-[44px] rounded-lg bg-[var(--primary)] px-4 py-2 text-white disabled:opacity-50">
                {submitting ? 'Registering…' : 'Register draft'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
